import { api } from "@/api/client"
import { ColorRank } from "../types/battle"
import {
  VirtualTournamentCurrent,
  VirtualTournamentDetail,
  VirtualTournamentHistoryItem,
} from "../types/virtualTournament"
import { GameCard } from "../types/battle"
import { guardiansLogApiError } from "../utils/guardiansDebugLog"
import { getGameApiErrorMessage } from "./gameApiErrors"
import { normalizeGameCards } from "../utils/gameCards"
import {
  normalizeVirtualTournamentCurrent,
  normalizeVirtualTournamentDetail,
  normalizeVirtualTournamentHistoryItem,
} from "../utils/virtualTournamentNormalize"

function unwrapGamePayload(data: unknown): Record<string, unknown> {
  if (!data || typeof data !== "object" || Array.isArray(data)) return {}
  const obj = data as Record<string, unknown>
  const nested = obj.data ?? obj.result ?? obj.payload
  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    return nested as Record<string, unknown>
  }
  return obj
}

export const getVirtualTournamentCurrent =
  async (): Promise<VirtualTournamentCurrent | null> => {
    try {
      const { data } = await api.get<unknown>("/game/virtual-tournament/current")
      return normalizeVirtualTournamentCurrent(unwrapGamePayload(data))
    } catch (error) {
      guardiansLogApiError("GET /game/virtual-tournament/current", error)
      throw error
    }
  }

export const getVirtualTournamentCards = async (
  colorRank: ColorRank
): Promise<GameCard[]> => {
  try {
    const { data } = await api.get<unknown>("/game/virtual-tournament/cards", {
      params: { colorRank, color_rank: colorRank },
    })
    return normalizeGameCards(data)
  } catch (error) {
    guardiansLogApiError("GET /game/virtual-tournament/cards", error)
    throw error
  }
}

export const registerVirtualTournament = async (payload: {
  colorRank: ColorRank
  cardId: string
}): Promise<VirtualTournamentCurrent | null> => {
  try {
    const { data } = await api.post<unknown>(
      "/game/virtual-tournament/register",
      {
        colorRank: payload.colorRank,
        color_rank: payload.colorRank,
        cardId: payload.cardId,
        card_id: payload.cardId,
      }
    )
    return (
      normalizeVirtualTournamentCurrent(unwrapGamePayload(data)) ??
      (await getVirtualTournamentCurrent())
    )
  } catch (error) {
    guardiansLogApiError("POST /game/virtual-tournament/register", error)
    throw new Error(
      getGameApiErrorMessage(error, "Не удалось зарегистрироваться на турнир")
    )
  }
}

export const cancelVirtualTournament = async (payload: {
  colorRank: ColorRank
}): Promise<VirtualTournamentCurrent | null> => {
  try {
    const { data } = await api.post<unknown>(
      "/game/virtual-tournament/cancel",
      {
        colorRank: payload.colorRank,
        color_rank: payload.colorRank,
      }
    )
    return (
      normalizeVirtualTournamentCurrent(unwrapGamePayload(data)) ??
      (await getVirtualTournamentCurrent())
    )
  } catch (error) {
    guardiansLogApiError("POST /game/virtual-tournament/cancel", error)
    throw new Error(
      getGameApiErrorMessage(error, "Не удалось отменить участие")
    )
  }
}

export const getVirtualTournamentDetail = async (
  tournamentId: string
): Promise<VirtualTournamentDetail | null> => {
  try {
    const { data } = await api.get<unknown>(
      `/game/virtual-tournament/${tournamentId}`
    )
    return normalizeVirtualTournamentDetail(unwrapGamePayload(data))
  } catch (error) {
    guardiansLogApiError(`GET /game/virtual-tournament/${tournamentId}`, error)
    throw error
  }
}

export const getVirtualTournamentHistory = async (params?: {
  take?: number
}): Promise<VirtualTournamentHistoryItem[]> => {
  try {
    const { data } = await api.get<unknown>(
      "/game/virtual-tournament/history",
      { params: { take: params?.take ?? 20 } }
    )
    const obj = unwrapGamePayload(data)
    const raw = Array.isArray(obj)
      ? obj
      : Array.isArray(obj.items)
        ? obj.items
        : Array.isArray(obj.history)
          ? obj.history
          : []
    return raw
      .map(normalizeVirtualTournamentHistoryItem)
      .filter((item): item is VirtualTournamentHistoryItem => item != null)
  } catch (error) {
    guardiansLogApiError("GET /game/virtual-tournament/history", error)
    throw error
  }
}